import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { todayIST, formatIST } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DownloadSimple, ArrowsOutSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttendanceTimeline from "@/components/AttendanceTimeline";

export default function Attendance() {
  const [date, setDate] = useState(todayIST());
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);

  async function load() {
    const { data } = await api.get(`/attendance/all?date=${date}`);
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  async function downloadZip() {
    try {
      const res = await api.get(`/attendance/zip?date=${date}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `attendance-${date}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download ZIP"); }
  }

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Live</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Attendance Log</h1>
        </div>
        <div className="flex gap-3">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 data-testid="att-date-picker"
                 className="bg-[#16161C] border-[#2D2D35] h-10 w-44 [color-scheme:dark]" />
          <Button onClick={downloadZip} className="bg-[#E50914] hover:bg-[#C80812] h-10" data-testid="att-zip-download">
            <DownloadSimple size={16} className="mr-1" /> Download ZIP
          </Button>
        </div>
      </div>

      <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
        <table className="w-full" data-testid="attendance-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">Shift</th>
              <th className="text-left p-3">IN</th>
              <th className="text-left p-3">OUT</th>
              <th className="text-right p-3">Worked</th>
              <th className="text-right p-3">Late (m)</th>
              <th className="text-right p-3">Status</th>
              <th className="text-right p-3">Review</th>
            </tr>
          </thead>
          <tbody className="font-mono text-sm">
            {rows.map((r) => {
              const c = r.computed || {};
              return (
                <tr key={r.id} onClick={() => setActive(r)}
                    data-testid={`att-row-${r.employee_id}`}
                    className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24] cursor-pointer">
                  <td className="p-3">
                    <div className="text-white text-sm">{r.employee_name}</div>
                    <div className="text-[10px] text-zinc-500">{r.employee_id}</div>
                  </td>
                  <td className="p-3 text-zinc-300 text-xs">{r.shift}</td>
                  <td className="p-3 text-xs">{formatIST(r.in_at, "full")}</td>
                  <td className="p-3 text-xs">{formatIST(r.out_at, "full")}</td>
                  <td className="p-3 text-right">{c.worked_hours || 0}h</td>
                  <td className="p-3 text-right text-[#F59E0B]">{c.late_minutes || 0}</td>
                  <td className="p-3 text-right text-xs">
                    <Pill status={c.status} />
                  </td>
                  <td className="p-3 text-right text-xs">
                    <span className="inline-flex items-center gap-2">
                      <Pill status={r.review_status} />
                      <ArrowsOutSimple size={14} className="text-zinc-500" />
                    </span>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan="8" className="text-center text-zinc-500 p-8 text-sm">No attendance for {date}.</td></tr>}
          </tbody>
        </table>
      </div>

      <AttendanceTimeline record={active} open={!!active} onClose={() => setActive(null)} />
    </div>
  );
}

function Pill({ status }) {
  const map = {
    PRESENT: "bg-[#22C55E]/15 text-[#22C55E]",
    HALF_DAY: "bg-[#F59E0B]/15 text-[#F59E0B]",
    ABSENT: "bg-[#FF3B30]/15 text-[#FF3B30]",
    IN_PROGRESS: "bg-zinc-500/15 text-zinc-300",
    APPROVED: "bg-[#22C55E]/15 text-[#22C55E]",
    REJECTED: "bg-[#FF3B30]/15 text-[#FF3B30]",
    PENDING: "bg-zinc-500/15 text-zinc-300",
    CLARIFICATION: "bg-[#F59E0B]/15 text-[#F59E0B]",
  };
  if (!status) return <span className="text-zinc-600">—</span>;
  return <span className={`px-2 py-1 rounded ${map[status] || "bg-zinc-700/30 text-zinc-300"}`}>{status}</span>;
}
